// ── 신규 콜 등록 폼 — 데스크톱 2줄 격자 스타일 (2026-09-15 시안) ──────────────
// 1줄: 담당자 · 상품 · 방문일자 · 미팅시간 · 내용(넓게) · 마케팅 담당자 · 등록
// 2줄: 네이버 플레이스 · 상호 · 업종 · 주소(넓게) · 전화번호 · 휴대폰 · 대표자
// 라벨 칸(홀수 트랙)은 두 줄이 같은 세로선에 맞고, 내용·주소만 서로 다른 칸을 가로질러 넓어진다.
// 모양(2차 시안): 겉 테두리 없음 · 라벨은 떨어진 검정 알약 · 입력은 둥근 박스 · 두 줄 사이 점선.
// dark mode: 라벨은 본문색 배경 + 카드색 글자라 테마에 따라 자동 반전된다.

const LABEL = '50px';

/** 16트랙: 라벨 8개(고정) + 입력 8개(시안 폭 비율).
 *  최소 px 는 잘리면 안 되는 칸(선택·날짜·전화번호·고정 체크·등록 버튼)을 지키고,
 *  좁은 화면에서 줄어드는 몫은 두 칸을 가로지르는 내용·주소가 맡는다(1400px 에서 합계가 폭에 맞음). */
export const REG_GRID_TEMPLATE = [
  LABEL, 'minmax(96px, 150fr)',   // 1-2  담당자 / 네이버 플레이스
  LABEL, 'minmax(96px, 154fr)',   // 3-4  상품 / 상호 ("상품 선택"이 화살표에 안 가리는 폭)
  LABEL, 'minmax(124px, 219fr)',  // 5-6  방문일자 / 업종
  LABEL, 'minmax(64px, 172fr)',   // 7-8  미팅시간 / 주소
  LABEL, 'minmax(4px, 109fr)',    // 9-10 내용 / (주소) — 위 두 칸에 준 8px 을 여기서 뺀다
  LABEL, 'minmax(108px, 208fr)',  // 11-12 (내용) / 전화번호
  LABEL, 'minmax(124px, 212fr)',  // 13-14 마케팅 담당자 / 휴대폰
  LABEL, 'minmax(86px, 149fr)',   // 15-16 등록 / 대표자
].join(' ');

/** 외곽: 테두리 없음, 좁은 화면에서만 가로 스크롤 */
export const regWrapStyle = {
  overflowX: 'auto',
  overflowY: 'clip',
};

/** 격자 행: 1줄 · 점선 · 2줄 */
export const regGridStyle = {
  display: 'grid',
  gridTemplateColumns: REG_GRID_TEMPLATE,
  gridTemplateRows: 'auto auto auto',
};

// 논리 줄(1·2) → 격자 행(1·3). 가운데 2행은 점선 자리다.
const gridRowOf = (row) => (row === 2 ? 3 : 1);

/** 두 줄 사이 연한 점선 (격자 전체 폭) */
export const regRowDivider = {
  gridRow: 2,
  gridColumn: '1 / -1',
  borderTop: '1px dashed var(--border)',
};

/** 라벨: 검정 알약 · 흰 글씨 · 두 단어는 줄바꿈(네이버/플레이스).
 *  칸 자체에 위아래 margin 을 줘서 입력 박스와 같은 높이의 알약이 된다. */
export const regLabelCell = (row, col) => ({
  gridRow: gridRowOf(row),
  gridColumn: col,
  margin: '6px 0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 2px',
  minHeight: 32,
  borderRadius: 6,
  background: 'var(--text)',
  color: 'var(--bg-card)',
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1.2,
  textAlign: 'center',
  wordBreak: 'keep-all',
  boxSizing: 'border-box',
});

/** 입력 칸: 배경 없음, 라벨과 좌우로 띄운다 */
export const regInputCell = (row, col) => ({
  gridRow: gridRowOf(row),
  gridColumn: col,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 8px',
  minWidth: 0,
  boxSizing: 'border-box',
});

/** 칸 안 입력 필드: 연한 테두리의 둥근 박스 */
export const regInput = {
  width: '100%',
  minWidth: 0,
  minHeight: 32,
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '4px 6px',
  fontSize: 12,
  boxSizing: 'border-box',
  display: 'block',
  color: 'var(--text)',
  background: 'var(--bg-card)',
};

/** 네이버 플레이스 붙여넣기: 파란 점선 박스 + 회색 안내 글씨 */
export const regNaverInput = {
  ...regInput,
  height: 32,
  resize: 'none',
  overflow: 'hidden',
  padding: '7px 6px 0',
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  textAlign: 'center',
  border: '1px dashed var(--accent)',
  background: 'var(--std-badge-bg, rgba(59, 154, 242, 0.08))',
  color: 'var(--text-muted)',
  fontWeight: 400,
};

/** 내용: 입력하면 높이가 늘어나는 textarea (useCallRegisterHandlers 가 높이 조절) */
export const regContentInput = { ...regInput, resize: 'none', overflow: 'hidden', lineHeight: 1.4 };

/** 마케팅 담당자 옆 고정 체크 */
export const regFixedLabel = {
  display: 'inline-flex', gap: 4, alignItems: 'center', flexShrink: 0,
  whiteSpace: 'nowrap', fontSize: 12, fontWeight: 700, color: 'var(--text-sub)',
};

/** 등록하기: 흰 바탕 + 검정 테두리, 칸 폭을 채운다 (다크 모드에서는 색이 함께 반전) */
export const regSubmitBtn = {
  width: '100%',
  minHeight: 32,
  border: '1.5px solid var(--text)',
  background: 'var(--bg-card)',
  color: 'var(--text)',
  borderRadius: 8,
  padding: '4px 8px',
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
};
